import React, { useRef, useEffect } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import diveSitesData from '../../../data/dive-sites.json'

// Fix for production build - use CDN worker to avoid minification issues
maplibregl.workerUrl = '/maplibre-gl-csp-worker.js'

const DARK_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json'

// Map GeoJSON names to image database site names
const GEOJSON_TO_DB_NAME = {
  'House Reef (Crystal Blue Resort)': 'CBR House Reef',
}

const DB_TO_GEOJSON_NAME = {
  'CBR House Reef': 'House Reef (Crystal Blue Resort)',
}

// Convert GeoJSON name to database name
const toDbName = (geojsonName) => GEOJSON_TO_DB_NAME[geojsonName] || geojsonName

// Convert database name to GeoJSON name
const toGeojsonName = (dbName) => DB_TO_GEOJSON_NAME[dbName] || dbName

export function DiveSiteMap({ currentSite, siteNames, onSiteSelect }) {
  const mapContainer = useRef(null)
  const map = useRef(null)
  
  // Store callbacks in refs to avoid effect re-runs
  const siteNamesRef = useRef(siteNames)
  const onSiteSelectRef = useRef(onSiteSelect)
  const currentSiteRef = useRef(currentSite)
  
  // Keep refs updated
  useEffect(() => {
    siteNamesRef.current = siteNames
    onSiteSelectRef.current = onSiteSelect
    currentSiteRef.current = currentSite
  }, [siteNames, onSiteSelect, currentSite])

  // Initialize map only once
  useEffect(() => {
    if (map.current) return

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: DARK_STYLE,
      center: [120.87, 13.715],  // centroid of dive sites
      zoom: 11,
      minZoom: 11,
      maxZoom: 16,
      maxBounds: [
        [120.78, 13.64],  // SW - tight bounds for landscape container
        [120.96, 13.79],  // NE
      ],
      attributionControl: false,
    })

    map.current.on('load', () => {
      map.current.addSource('dive-sites', {
        type: 'geojson',
        data: diveSitesData,
      })

      // Add circle layer for all sites
      map.current.addLayer({
        id: 'dive-sites-circles',
        type: 'circle',
        source: 'dive-sites',
        paint: {
          'circle-radius': 8,
          'circle-color': '#888',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff',
        },
      })

      // Add labels
      map.current.addLayer({
        id: 'dive-sites-labels',
        type: 'symbol',
        source: 'dive-sites',
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Open Sans Regular'],
          'text-size': 12,
          'text-offset': [0, 1.5],
          'text-anchor': 'top',
        },
        paint: {
          'text-color': '#fff',
          'text-halo-color': '#000',
          'text-halo-width': 1,
        },
      })

      // Click handler for markers (uses refs for latest values)
      map.current.on('click', 'dive-sites-circles', (e) => {
        const clickedGeojsonName = e.features[0].properties.name
        const dbName = toDbName(clickedGeojsonName)
        const siteIndex = siteNamesRef.current.findIndex(name => name === dbName)
        if (siteIndex !== -1 && onSiteSelectRef.current) {
          onSiteSelectRef.current(siteIndex)
        }
      })

      // Change cursor on hover
      map.current.on('mouseenter', 'dive-sites-circles', () => {
        map.current.getCanvas().style.cursor = 'pointer'
      })
      map.current.on('mouseleave', 'dive-sites-circles', () => {
        map.current.getCanvas().style.cursor = ''
      })

      // Apply initial highlighting for the already-selected site
      const geojsonName = toGeojsonName(currentSiteRef.current || '')
      map.current.setPaintProperty('dive-sites-circles', 'circle-color', [
        'case',
        ['==', ['get', 'name'], geojsonName],
        '#fff',
        '#888',
      ])
      map.current.setPaintProperty('dive-sites-circles', 'circle-radius', [
        'case',
        ['==', ['get', 'name'], geojsonName],
        10,
        8,
      ])
    })

    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, []) // Empty deps - only run once

  // Update highlighted site when currentSite changes
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded()) return

    const source = map.current.getSource('dive-sites')
    if (!source) return

    // Convert DB name to GeoJSON name for comparison
    const geojsonName = toGeojsonName(currentSite || '')

    // Update circle colors based on selected site
    map.current.setPaintProperty('dive-sites-circles', 'circle-color', [
      'case',
      ['==', ['get', 'name'], geojsonName],
      '#fff',
      '#888',
    ])
    map.current.setPaintProperty('dive-sites-circles', 'circle-radius', [
      'case',
      ['==', ['get', 'name'], geojsonName],
      10,
      8,
    ])
  }, [currentSite])

  return <div ref={mapContainer} className="dive-site-map" />
}
